import { produce } from 'immer';

import type { ChatWidgetAction } from '../../actions/chatWidgets';

export type ChatWidget = {
  id: string | null;
  organization_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  name: string;
  updates?: any;
};

export type ChatWidgetState = {
  meta: any & {
    chatWidgetModalShown: boolean;
    validateQueue: string | null;
    hasUnsavedChanges: boolean;
    selectedWidget: ChatWidget;
    selectedWidgetTouched: boolean;
    deleteWidgetModalShown: boolean;
    validateName: string | null;
  };
  widgets: Array<ChatWidget>;
};

const initialState: ChatWidgetState = {
  meta: {
    asyncStatus: 'succeeded',
    chatWidgetModalShown: false,
    hasUnsavedChanges: false,
    selectedWidget: emptyWidget(),
    selectedWidgetTouched: false,
    deleteWidgetModalShown: false,
    validateName: null,
    validateQueue: null,
    previousPage: null,
    nextPage: null,
    hideStrategyValues: [],
  },
  widgets: [],
};

function emptyWidget(): ChatWidget {
  return {
    id: null,
    organization_id: null,
    created_at: null,
    updated_at: null,
    name: '',
  };
}

export function chatWidget(
  state: ChatWidgetState = initialState,
  action: ChatWidgetAction,
): ChatWidgetState {
  switch (action.type) {
    case 'CHAT_ADD_NEW_WIDGET': {
      return {
        ...state,
        meta: {
          ...state.meta,
          selectedWidget: emptyWidget(),
          chatWidgetModalShown: true,
        },
      };
    }
    case 'CHAT_CREATE_WIDGET': {
      return {
        ...state,
        meta: { ...state.meta, chatWidgetModalShown: false },
        widgets: [action.widget, ...state.widgets],
      };
    }

    case 'WIDGET_CHANGE_NAME': {
      return {
        ...state,
        meta: {
          ...state.meta,
          hasUnsavedChanges: true,
          selectedWidget: { ...state.meta.selectedWidget, name: action.name },
        },
      };
    }

    case 'WIDGET_CHANGE_QUEUE': {
      return {
        ...state,
        meta: {
          ...state.meta,
          hasUnsavedChanges: true,
          selectedWidget: {
            ...state.meta.selectedWidget,
            queue_id: action.queueId,
          },
        },
      };
    }

    case 'VALIDATE_WIDGET_NAME': {
      return { ...state, meta: { ...state.meta, validateName: action.error } };
    }

    case 'VALIDATE_WIDGET_QUEUE': {
      return { ...state, meta: { ...state.meta, validateQueue: action.error } };
    }

    case 'CHAT_CREATE_WIDGET_SUCCEEDED': {
      const localWidget = action.localWidget;
      const createdWidget = action.widget;
      return {
        ...state,
        meta: {
          ...state.meta,
          hasUnsavedChanges: false,
          chatWidgetModalShown: false,
          validateName: null,
          validateQueue: null,
          selectedWidget: emptyWidget(),
        },
        widgets: state.widgets.map((x) =>
          x === localWidget ? createdWidget : x,
        ),
      };
    }
    case 'CHAT_CREATE_WIDGET_FAILED': {
      const widgetId = action.widget.id;
      return {
        ...state,
        meta: {
          ...state.meta,
          hasUnsavedChanges: true,
          chatWidgetModalShown: false,
          selectedWidget: emptyWidget(),
        },
        widgets: state.widgets.filter((x) => x.id !== widgetId),
      };
    }
    case 'CHAT_DELETE_WIDGET': {
      const deletedWidget = action.widget;
      return {
        ...state,
        meta: { ...state.meta, deleteWidgetModalShown: false },
        widgets: state.widgets.filter((x) => x.id !== deletedWidget.id),
      };
    }

    case 'CHAT_DELETE_WIDGET_FAILED': {
      const readdedWidgets = [...state.widgets];
      readdedWidgets.splice(action.index, 0, action.widget);
      return { ...state, widgets: readdedWidgets };
    }
    case 'CHAT_HIDE_CHAT_WIDGET_MODAL': {
      return {
        ...state,
        meta: {
          ...state.meta,
          chatWidgetModalShown: false,
          selectedWidget: emptyWidget(),
        },
      };
    }
    case 'CHAT_UPDATE_WIDGET_SUCCEEDED': {
      return produce(state, (draftState) => {
        const { widget, widgetId } = action;
        const widgetIndex = state.widgets.findIndex((w) => w.id === widgetId);
        draftState.widgets[widgetIndex] = {
          ...state.widgets[widgetIndex],
          ...widget.updates,
        };
        if (state.meta.selectedWidget.id === widgetId) {
          draftState.meta.selectedWidget = draftState.widgets[widgetIndex];
        }
        draftState.meta.hasUnSavedChanges = false;
        draftState.meta.asyncStatus = 'succeeded';
      });
    }

    case 'CHAT_UPDATE_WIDGET_FAILED': {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    }

    case 'CHAT_READ_WIDGET_SUCCEEDED': {
      const readWidget = action.widget;

      return {
        ...state,
        meta: {
          ...state.meta,
          hasUnsavedChanges: false,
          validateName: null,
          validateQueue: null,
          selectedWidget: action.widget,
        },
        widgets: [
          action.widget,
          ...state.widgets.filter((x) => x.id !== readWidget.id),
        ],
      };
    }

    case 'CHAT_READ_WIDGETS': {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    }

    case 'CHAT_READ_WIDGETS_SUCCEEDED': {
      return {
        ...state,
        widgets: action.widgets,
        meta: {
          ...state.meta,
          asyncStatus: 'succeeded',
          nextPage:
            action.meta && action.meta.next_page ? action.meta.next_page : null,
          previousPage:
            action.meta && action.meta.prev_page ? action.meta.prev_page : null,
        },
      };
    }
    case 'CHAT_SHOW_DELETE_WIDGET_MODAL': {
      return {
        ...state,
        meta: {
          ...state.meta,
          selectedWidget: action.widget,
          deleteWidgetModalShown: true,
        },
      };
    }
    case 'CHAT_HIDE_DELETE_WIDGET_MODAL': {
      return {
        ...state,
        meta: {
          ...state.meta,
          selectedWidget: emptyWidget(),
          deleteWidgetModalShown: false,
        },
      };
    }
    default:
      return state;
  }
}
